import { useEffect, useState, type FormEvent } from "react";

import { exitSurvey, type Bilingual, type Question } from "./exitSurveyContent";
import "./exitSurvey.css";

/**
 * Public, unauthenticated whānau exit-feedback survey (ADR-008). Opened from a
 * shared/SMS link of the form `/survey/exit#t=<token>`. No MSAL — this route is
 * a sibling of <App />. Submits anonymously to the public API; the token only
 * gates the link and lets the server attach coarse cohort tags server-side.
 *
 * te reo strings are draft (see exitSurveyContent.ts); until reviewed, `mi`
 * falls back to `en`, and the default language is English. Flip `INITIAL_LANG`
 * to "mi" once Des has signed off the translations.
 */

const API_BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  "http://localhost:7071/api/v1";

const INITIAL_LANG: Lang = "en";

type Lang = "mi" | "en";
type ServiceOpt = { value: string; label: string };

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; services: ServiceOpt[] }
  | { kind: "invalid" }
  | { kind: "error"; message: string };

/**
 * DEV-only preview: open /survey/exit?preview=1 to view the form on sample data
 * with no API or token. Compiled out of production (import.meta.env.DEV gate),
 * mirroring the reporting VITE_REPORTS_USE_MOCK pattern.
 */
const PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get("preview") === "1";

const PREVIEW_SERVICES: ServiceOpt[] = [
  { value: "whanau-resilience", label: "Whānau Resilience" },
  { value: "tamariki-ora", label: "Tamariki Ora" },
  { value: "family-start", label: "Family Start" },
  { value: "health-navigator", label: "Health Navigator" },
];

function readToken(): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  const t = new URLSearchParams(hash).get("t");
  return t && t.trim() ? t.trim() : null;
}

export function ExitSurveyPage() {
  const [lang, setLang] = useState<Lang>(INITIAL_LANG);
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [text, setText] = useState<Record<string, string>>({});
  const [multi, setMulti] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const token = readToken();

  // Resolve a bilingual string for the current language (mi falls back to en).
  const tr = (b: Bilingual): string => (lang === "mi" ? b.mi : b.en) ?? b.en;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    if (PREVIEW) {
      setLoad({ kind: "ready", services: PREVIEW_SERVICES });
      return;
    }
    if (!token) {
      setLoad({ kind: "invalid" });
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/public/survey/exit?token=${encodeURIComponent(token)}`,
          { headers: { accept: "application/json" } },
        );
        if (cancelled) return;
        if (res.status === 410 || res.status === 400) {
          setLoad({ kind: "invalid" });
          return;
        }
        if (!res.ok) {
          setLoad({ kind: "error", message: `HTTP ${res.status}` });
          return;
        }
        const data = (await res.json()) as { services?: ServiceOpt[] };
        setLoad({ kind: "ready", services: data.services ?? [] });
      } catch (err) {
        if (cancelled) return;
        setLoad({
          kind: "error",
          message: err instanceof Error ? err.message : "Network error",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  function setTextVal(id: string, v: string) {
    setText((prev) => ({ ...prev, [id]: v }));
  }
  function toggleMulti(id: string, value: string) {
    setMulti((prev) => {
      const cur = prev[id] ?? [];
      return {
        ...prev,
        [id]: cur.includes(value)
          ? cur.filter((v) => v !== value)
          : [...cur, value],
      };
    });
  }

  function ethnicityValue(): string | undefined {
    const v = text["primary_ethnicity"];
    if (v === "__other")
      return text["primary_ethnicity_other"]?.trim() || undefined;
    return v || undefined;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (PREVIEW) {
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);

    const num = (id: string): number | undefined => {
      const raw = text[id];
      if (!raw) return undefined;
      const n = Number(raw);
      return Number.isFinite(n) ? n : undefined;
    };

    const body = {
      token,
      language: lang,
      ratings,
      text: {
        most_proud: text["most_proud"] || undefined,
        what_helped: text["what_helped"] || undefined,
        got_in_way: text["got_in_way"] || undefined,
        improve: text["improve"] || undefined,
      },
      demographics: {
        gender: text["gender"] || undefined,
        age_range: text["age_range"] || undefined,
        primary_ethnicity: ethnicityValue(),
        iwi: text["iwi"] || undefined,
        services_involved: multi["services_involved"] ?? undefined,
        time_with_service: text["time_with_service"] || undefined,
        whanau_size: num("whanau_size"),
        num_benefited: num("num_benefited"),
      },
      contact: {
        name: text["contact_name"] || undefined,
        detail: text["contact_detail"] || undefined,
      },
    };

    try {
      const res = await fetch(`${API_BASE}/public/survey/exit`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify(body),
      });
      if (res.status === 201) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (res.status === 410) {
        setSubmitError(
          tr({
            en: "This survey link has already been used or has expired.",
            mi: null,
          }),
        );
        return;
      }
      setSubmitError(
        tr({ en: "Sorry, something went wrong. Please try again.", mi: null }),
      );
    } catch {
      setSubmitError(
        tr({
          en: "We couldn't send your answers. Please check your connection and try again.",
          mi: null,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const otherLabel = tr({ en: "Other", mi: null });

  function renderQuestion(q: Question, services: ServiceOpt[]) {
    const prompt = tr(q.prompt);
    switch (q.kind) {
      case "rating":
        return (
          <fieldset className="esx-q esx-rating" key={q.id}>
            <legend className="esx-q__label">{prompt}</legend>
            <div className="esx-rating__row">
              <span className="esx-rating__anchor">{tr(q.lowAnchor)}</span>
              <div className="esx-rating__opts">
                {[1, 2, 3, 4, 5].map((n) => (
                  <label key={n} className="esx-rating__opt">
                    <input
                      type="radio"
                      name={q.id}
                      value={n}
                      checked={ratings[q.id] === n}
                      onChange={() =>
                        setRatings((prev) => ({ ...prev, [q.id]: n }))
                      }
                    />
                    <span aria-hidden="true">{n}</span>
                    <span className="esx-sr">{`${n} / 5`}</span>
                  </label>
                ))}
              </div>
              <span className="esx-rating__anchor">{tr(q.highAnchor)}</span>
            </div>
          </fieldset>
        );
      case "text":
        return (
          <div className="esx-q" key={q.id}>
            <label className="esx-q__label" htmlFor={q.id}>
              {prompt}
            </label>
            <textarea
              id={q.id}
              className="esx-textarea"
              maxLength={q.maxLength}
              rows={3}
              value={text[q.id] ?? ""}
              onChange={(e) => setTextVal(q.id, e.target.value)}
            />
          </div>
        );
      case "shortText":
        return (
          <div className="esx-q" key={q.id}>
            <label className="esx-q__label" htmlFor={q.id}>
              {prompt}
            </label>
            <input
              id={q.id}
              type="text"
              className="esx-input"
              maxLength={q.maxLength}
              value={text[q.id] ?? ""}
              onChange={(e) => setTextVal(q.id, e.target.value)}
            />
          </div>
        );
      case "number":
        return (
          <div className="esx-q" key={q.id}>
            <label className="esx-q__label" htmlFor={q.id}>
              {prompt}
            </label>
            <input
              id={q.id}
              type="number"
              inputMode="numeric"
              className="esx-input esx-input--num"
              min={q.min}
              max={q.max}
              value={text[q.id] ?? ""}
              onChange={(e) => setTextVal(q.id, e.target.value)}
            />
          </div>
        );
      case "single": {
        const opts = [...q.options];
        return (
          <fieldset className="esx-q" key={q.id}>
            <legend className="esx-q__label">{prompt}</legend>
            <div className="esx-choices">
              {opts.map((o) => (
                <label key={o.value} className="esx-choice">
                  <input
                    type="radio"
                    name={q.id}
                    value={o.value}
                    checked={text[q.id] === o.value}
                    onChange={() => setTextVal(q.id, o.value)}
                  />
                  <span>{tr(o.label)}</span>
                </label>
              ))}
              {q.allowOther && (
                <label className="esx-choice">
                  <input
                    type="radio"
                    name={q.id}
                    value="__other"
                    checked={text[q.id] === "__other"}
                    onChange={() => setTextVal(q.id, "__other")}
                  />
                  <span>{otherLabel}</span>
                </label>
              )}
            </div>
            {q.allowOther && text[q.id] === "__other" && (
              <input
                type="text"
                aria-label={otherLabel}
                className="esx-input"
                maxLength={80}
                value={text[`${q.id}_other`] ?? ""}
                onChange={(e) => setTextVal(`${q.id}_other`, e.target.value)}
              />
            )}
          </fieldset>
        );
      }
      case "multi": {
        const opts =
          q.optionsSource === "service-directory" ? services : q.options;
        return (
          <fieldset className="esx-q" key={q.id}>
            <legend className="esx-q__label">{prompt}</legend>
            <div className="esx-choices">
              {opts.length === 0 && (
                <p className="esx-muted">
                  {tr({ en: "No options available.", mi: null })}
                </p>
              )}
              {opts.map((o) => {
                const label =
                  typeof o.label === "string" ? o.label : tr(o.label);
                return (
                  <label key={o.value} className="esx-choice">
                    <input
                      type="checkbox"
                      value={o.value}
                      checked={(multi[q.id] ?? []).includes(o.value)}
                      onChange={() => toggleMulti(q.id, o.value)}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      }
    }
  }

  // ---- render ----------------------------------------------------------

  const langToggle = (
    <div className="esx-lang" role="group" aria-label="Language">
      <button
        type="button"
        className={lang === "mi" ? "esx-lang__btn is-active" : "esx-lang__btn"}
        onClick={() => setLang("mi")}
        lang="mi"
      >
        Te Reo Māori
      </button>
      <button
        type="button"
        className={lang === "en" ? "esx-lang__btn is-active" : "esx-lang__btn"}
        onClick={() => setLang("en")}
      >
        English
      </button>
    </div>
  );

  if (load.kind === "invalid") {
    return (
      <main className="esx esx--message">
        <h1>{tr({ en: "This survey link isn't valid", mi: null })}</h1>
        <p>
          {tr({
            en: "The link may have expired or already been used. If you'd still like to share feedback, please contact your kaimahi.",
            mi: null,
          })}
        </p>
      </main>
    );
  }

  if (load.kind === "error") {
    return (
      <main className="esx esx--message">
        <h1>{tr({ en: "Something went wrong", mi: null })}</h1>
        <p>{tr({ en: "Please try the link again shortly.", mi: null })}</p>
      </main>
    );
  }

  if (load.kind === "loading") {
    return (
      <main className="esx esx--message" aria-busy="true">
        <p>{tr({ en: "Loading…", mi: null })}</p>
      </main>
    );
  }

  if (submitted) {
    return (
      <main className="esx esx--message">
        <h1>{tr({ en: "Ngā mihi nui", mi: "Ngā mihi nui" })}</h1>
        <p>{tr(exitSurvey.closing)}</p>
      </main>
    );
  }

  return (
    <main className="esx">
      <header className="esx-header">
        {langToggle}
        <h1 className="esx-title">{tr(exitSurvey.title)}</h1>
        {exitSurvey.intro.map((line, i) => (
          <p
            key={i}
            className="esx-intro"
            lang={lang === "mi" && line.mi ? "mi" : undefined}
          >
            {tr(line)}
          </p>
        ))}
        <p className="esx-note">{tr(exitSurvey.confidentialityNote)}</p>
      </header>

      <form onSubmit={handleSubmit} noValidate>
        {exitSurvey.sections.map((section) => (
          <section className="esx-section" key={section.id}>
            <h2 className="esx-section__title">{tr(section.title)}</h2>
            {section.id === "keep_in_touch" && (
              <p className="esx-note">{tr(exitSurvey.leaveNameNote)}</p>
            )}
            {section.questions.map((q) => renderQuestion(q, load.services))}
          </section>
        ))}

        {submitError && (
          <p className="esx-error" role="alert">
            {submitError}
          </p>
        )}

        <div className="esx-actions">
          <button type="submit" className="esx-submit" disabled={submitting}>
            {submitting
              ? tr({ en: "Sending…", mi: null })
              : tr({ en: "Submit feedback", mi: null })}
          </button>
        </div>
      </form>
    </main>
  );
}

export default ExitSurveyPage;
